import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Calculate as CalculateIcon,
  DateRange as DateRangeIcon,
  CalendarMonth as CalendarMonthIcon,
} from '@mui/icons-material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { usePeriodoStore } from '../store';
import { MESES, ESTADOS_PERIODO } from '../types/periodo';
import { PeriodoService } from '../services/periodoService';
import EditPeriodo from './EditPeriodo';

const PeriodosPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    periodos,
    loading,
    dialogOpen,
    fetchPeriodos,
    updateEstado,
    openDialog,
    closeDialog,
  } = usePeriodoStore();

  // Dialog states for weekly generation
  const [semanalOpen, setSemanalOpen] = useState(false);
  const [semanalAnio, setSemanalAnio] = useState(new Date().getFullYear());
  const [semanalUIT, setSemanalUIT] = useState(5350);
  const [semanalRMV, setSemanalRMV] = useState(1025);
  const [semanalLoading, setSemanalLoading] = useState(false);

  // Dialog states for biweekly generation
  const [quincenalOpen, setQuincenalOpen] = useState(false);
  const [quincenalAnio, setQuincenalAnio] = useState(new Date().getFullYear());
  const [quincenalMes, setQuincenalMes] = useState(new Date().getMonth() + 1);
  const [quincenalUIT, setQuincenalUIT] = useState(5350);
  const [quincenalRMV, setQuincenalRMV] = useState(1025);
  const [quincenalLoading, setQuincenalLoading] = useState(false);

  useEffect(() => {
    fetchPeriodos();
  }, [fetchPeriodos]);

  const handleGenerarSemanales = async () => {
    try {
      setSemanalLoading(true);
      const result = await PeriodoService.generarSemanales(semanalAnio, semanalUIT, semanalRMV);
      const count = Array.isArray(result) ? result.length : result;
      alert(`Se generaron ${count} periodos semanales para el año ${semanalAnio}`);
      setSemanalOpen(false);
      fetchPeriodos();
    } catch (error) {
      // Error handled by errorService via interceptors
    } finally {
      setSemanalLoading(false);
    }
  };

  const handleGenerarQuincenales = async () => {
    try {
      setQuincenalLoading(true);
      const mesLabel = MESES.find(m => m.value === quincenalMes)?.label || quincenalMes;
      const result = await PeriodoService.generarQuincenales(quincenalAnio, quincenalMes, quincenalUIT, quincenalRMV);
      const count = Array.isArray(result) ? result.length : result;
      alert(`Se generaron ${count} periodos quincenales para ${mesLabel} ${quincenalAnio}`);
      setQuincenalOpen(false);
      fetchPeriodos();
    } catch (error) {
      // Error handled by errorService via interceptors
    } finally {
      setQuincenalLoading(false);
    }
  };

  const handleCambiarEstado = async (id: number, estadoActual: string) => {
    let nuevoEstado = '';
    if (estadoActual === 'ABIERTO') {
      nuevoEstado = 'CALCULADO';
    } else if (estadoActual === 'CALCULADO') {
      if (window.confirm('¿Está seguro de cerrar este periodo?')) {
        nuevoEstado = 'CERRADO';
      } else {
        return;
      }
    } else if (estadoActual === 'CERRADO') {
      if (window.confirm('¿Está seguro de reabrir este periodo? Se limpiarán las fechas de cálculo y cierre.')) {
        nuevoEstado = 'ABIERTO';
      } else {
        return;
      }
    }
    if (nuevoEstado) {
      await updateEstado(id, nuevoEstado);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'periodo',
      headerName: 'Periodo',
      width: 180,
      valueGetter: (_value, row) => {
        const mes = MESES.find(m => m.value === row.mes);
        return `${mes?.label || row.mes} ${row.anio}`;
      },
    },
    { field: 'tipoPeriodo', headerName: 'Tipo', width: 110 },
    {
      field: 'fechaInicio',
      headerName: 'Inicio',
      width: 110,
      valueFormatter: (value: string) => value ? new Date(value).toLocaleDateString('es-PE') : '',
    },
    {
      field: 'fechaFin',
      headerName: 'Fin',
      width: 110,
      valueFormatter: (value: string) => value ? new Date(value).toLocaleDateString('es-PE') : '',
    },
    {
      field: 'estado',
      headerName: 'Estado',
      width: 120,
      renderCell: (params) => {
        const estadoConfig = ESTADOS_PERIODO.find(e => e.value === params.value);
        return (
          <Chip
            label={estadoConfig?.label || params.value}
            size="small"
            color={estadoConfig?.color || 'default'}
            variant="outlined"
          />
        );
      },
    },
    {
      field: 'valorUIT',
      headerName: 'UIT',
      width: 100,
      valueFormatter: (value: number) => `S/ ${value?.toLocaleString('es-PE') || 0}`,
    },
    {
      field: 'valorRMV',
      headerName: 'RMV',
      width: 100,
      valueFormatter: (value: number) => `S/ ${value?.toLocaleString('es-PE') || 0}`,
    },
    {
      field: 'fechaCalculo',
      headerName: 'F. Cálculo',
      width: 130,
      valueFormatter: (value: string | null) => value ? new Date(value).toLocaleDateString('es-PE') : '-',
    },
    {
      field: 'fechaCierre',
      headerName: 'F. Cierre',
      width: 130,
      valueFormatter: (value: string | null) => value ? new Date(value).toLocaleDateString('es-PE') : '-',
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const { estado } = params.row;

        return (
          <Box>
            {estado === 'ABIERTO' && (
              <Tooltip title="Marcar como Calculado">
                <IconButton
                  size="small"
                  color="warning"
                  onClick={() => handleCambiarEstado(params.row.idPeriodo, estado)}
                >
                  <CalculateIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {estado === 'CALCULADO' && (
              <Tooltip title="Cerrar Periodo">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleCambiarEstado(params.row.idPeriodo, estado)}
                >
                  <LockIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {estado === 'CERRADO' && (
              <Tooltip title="Reabrir Periodo">
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => handleCambiarEstado(params.row.idPeriodo, estado)}
                >
                  <LockOpenIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, mb: 3, gap: { xs: 1.5, sm: 0 } }}>
        <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="bold">
          Periodos de Planilla
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            color="secondary"
            size={isMobile ? 'small' : 'medium'}
            startIcon={<DateRangeIcon />}
            onClick={() => setSemanalOpen(true)}
          >
            {isMobile ? 'Semanales' : 'Generar Semanales'}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            size={isMobile ? 'small' : 'medium'}
            startIcon={<CalendarMonthIcon />}
            onClick={() => setQuincenalOpen(true)}
          >
            {isMobile ? 'Quincenales' : 'Generar Quincenales'}
          </Button>
          <Button
            variant="contained"
            size={isMobile ? 'small' : 'medium'}
            startIcon={<AddIcon />}
            onClick={openDialog}
          >
            {isMobile ? 'Nuevo' : 'Nuevo Periodo'}
          </Button>
        </Box>
      </Box>

      <Paper sx={{ height: { xs: 'calc(100vh - 220px)', sm: 'calc(100vh - 200px)' } }}>
        <DataGrid
          rows={periodos}
          columns={columns}
          getRowId={(row) => row.idPeriodo}
          loading={loading}
          density={isMobile ? 'compact' : 'standard'}
          pageSizeOptions={[12, 24, 48]}
          initialState={{
            pagination: { paginationModel: { pageSize: 12 } },
          }}
          disableRowSelectionOnClick
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8fafc',
            },
          }}
        />
      </Paper>

      <EditPeriodo
        open={dialogOpen}
        onClose={closeDialog}
        fullScreen={isMobile}
      />

      {/* Dialog: Generar Semanales */}
      <Dialog open={semanalOpen} onClose={() => setSemanalOpen(false)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle>Generar Periodos Semanales</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField
            label="Año"
            type="number"
            value={semanalAnio}
            onChange={(e) => setSemanalAnio(Number(e.target.value))}
            fullWidth
          />
          <TextField
            label="Valor UIT"
            type="number"
            value={semanalUIT}
            onChange={(e) => setSemanalUIT(Number(e.target.value))}
            fullWidth
          />
          <TextField
            label="Valor RMV"
            type="number"
            value={semanalRMV}
            onChange={(e) => setSemanalRMV(Number(e.target.value))}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSemanalOpen(false)} disabled={semanalLoading}>
            Cancelar
          </Button>
          <Button onClick={handleGenerarSemanales} variant="contained" disabled={semanalLoading}>
            {semanalLoading ? 'Generando...' : 'Generar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Generar Quincenales */}
      <Dialog open={quincenalOpen} onClose={() => setQuincenalOpen(false)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle>Generar Periodos Quincenales</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField
            label="Año"
            type="number"
            value={quincenalAnio}
            onChange={(e) => setQuincenalAnio(Number(e.target.value))}
            fullWidth
          />
          <TextField
            select
            label="Mes"
            value={quincenalMes}
            onChange={(e) => setQuincenalMes(Number(e.target.value))}
            fullWidth
          >
            {MESES.map((m) => (
              <MenuItem key={m.value} value={m.value}>
                {m.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Valor UIT"
            type="number"
            value={quincenalUIT}
            onChange={(e) => setQuincenalUIT(Number(e.target.value))}
            fullWidth
          />
          <TextField
            label="Valor RMV"
            type="number"
            value={quincenalRMV}
            onChange={(e) => setQuincenalRMV(Number(e.target.value))}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuincenalOpen(false)} disabled={quincenalLoading}>
            Cancelar
          </Button>
          <Button onClick={handleGenerarQuincenales} variant="contained" disabled={quincenalLoading}>
            {quincenalLoading ? 'Generando...' : 'Generar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PeriodosPage;
